import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Avatar } from "@/lib/cosmetics";
import api from "@/lib/api";
import { Coins, DollarSign, Dices, Trophy, Swords, Crown, Wallet as WalletIcon, BarChart3, ShieldAlert, LogOut, Menu, X, Shield, Languages, ScrollText, Gem, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SupportChat from "@/components/SupportChat";
import DiamondCelebration from "@/components/DiamondCelebration";
import FeedbackButton from "@/components/FeedbackButton";
import ReferralNudge, { fireInviteNudge } from "@/components/ReferralNudge";
import SoundToggles from "@/components/SoundToggles";
import BrandLogo from "@/components/BrandLogo";
import { VoltBubble } from "@/components/Volt";
import { useTrack } from "@/context/GameFeel";

const DIAMOND_MIN = 15000;

function LoyaltyPill() {
  const [loy, setLoy] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/loyalty");
        if (on) { setLoy(data); if (data.celebrate_diamond) setCelebrate(true); }
      } catch { /* ignore */ }
    })();
    return () => { on = false; };
  }, [user?.coins, user?.cash, user?.loyalty_points]);
  const onClaimed = () => { setCelebrate(false); setLoy((l) => (l ? { ...l, celebrate_diamond: false } : l)); navigate("/vip"); setTimeout(() => fireInviteNudge(t, navigate, "diamond"), 2500); };
  if (!loy) return null;
  const isDiamond = (loy.tier_index ?? 0) >= 4;
  const toDiamond = Math.max(0, DIAMOND_MIN - (loy.points ?? 0));
  const pct = Math.min(100, Math.round(((loy.points ?? 0) / DIAMOND_MIN) * 100));
  return (
    <>
    <DiamondCelebration show={celebrate} bonus={loy.diamond_bonus} onClaimed={onClaimed} />
    <Link to="/vip" data-testid="loyalty-pill" title={isDiamond ? "Diamond VIP Lounge" : `${toDiamond.toLocaleString()} points to Diamond`}
      className={`hidden md:flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full border transition-colors duration-200 ${isDiamond ? "bg-gradient-to-r from-amber-400/20 to-fuchsia-500/20 border-amber-400/50 hover:border-amber-400" : "bg-white/5 border-white/10 hover:border-white/25"}`}>
      <Gem className={`w-4 h-4 ${isDiamond ? "text-amber-300" : "text-fuchsia-300"}`} />
      {isDiamond ? (
        <span className="text-xs font-bold text-amber-200">Diamond VIP</span>
      ) : (
        <span className="flex flex-col leading-none gap-1">
          <span className="text-[10px] font-semibold text-gray-300">{toDiamond.toLocaleString()} to Diamond</span>
          <span className="w-24 h-1 rounded-full bg-white/10 overflow-hidden">
            <span className="block h-full bg-gradient-to-r from-fuchsia-400 to-amber-300" style={{ width: `${pct}%` }} />
          </span>
        </span>
      )}
    </Link>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  useTrack("lobby");

  const cashOn = !!user?.cash_enabled;
  const cashPaths = ["/tournaments", "/duels"];
  const isCash = cashOn && cashPaths.some((p) => location.pathname.startsWith(p));
  const mode = isCash ? "cash" : "coins";

  const hideModeBanner = ["/casino", "/duels"].some((p) => location.pathname.startsWith(p));

  const nav = [
    { to: "/casino", label: t("nav_casino"), icon: Dices },
    { to: "/tournaments", label: t("nav_tournaments"), icon: Trophy },
    { to: "/duels", label: t("nav_duels"), icon: Swords },
    { to: "/battlepass", label: t("nav_battlepass"), icon: Crown },
    { to: "/vip", label: "VIP", icon: Gem },
    { to: "/invite", label: t("nav_invite"), icon: Gift },
    { to: "/wallet", label: t("nav_wallet"), icon: WalletIcon },
    { to: "/leaderboard", label: t("nav_leaderboard"), icon: BarChart3 },
    { to: "/responsible-gaming", label: t("nav_responsible"), icon: ShieldAlert },
  ];
  if (user?.role === "admin") nav.push({ to: "/admin", label: t("nav_admin"), icon: Shield });

  const cos = user?.cosmetics || {};

  const LangBtn = () => (
    <button data-testid="language-toggle" onClick={toggle}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold hover:bg-white/10 transition-colors duration-200">
      <Languages className="w-4 h-4" /> {lang.toUpperCase()}
    </button>
  );

  return (
    <div className={`min-h-screen bg-[#07080D] text-white mode-${mode}`}>
      <header className={`sticky top-0 z-50 glass border-b transition-colors duration-300 ${isCash ? "border-emerald-500/25" : "border-amber-500/25"}`}>
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <BrandLogo size="header" withWordmark wordmarkClass="text-lg hidden sm:block" testid="brand-logo-link" />

          <div data-testid="dual-wallet-container" className="flex items-center gap-2">
            <LoyaltyPill />
            <div data-testid="coins-balance-pill" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 shadow-[0_0_12px_rgba(245,158,11,0.15)]">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="font-mono font-semibold text-amber-300 text-sm">{(user?.coins ?? 0).toLocaleString()}</span>
            </div>
            {cashOn && (
              <div data-testid="cash-balance-pill" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.15)]">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                <span className="font-mono font-semibold text-emerald-300 text-sm">{(user?.cash ?? 0).toFixed(2)}</span>
              </div>
            )}
            <div className="hidden sm:block"><LangBtn /></div>
            <div className="hidden sm:block"><SoundToggles /></div>
            <Link to="/battlepass" title="Cosmetics" className="hidden sm:block ml-1">
              <Avatar avatarId={cos.equipped_avatar} frameId={cos.equipped_frame} size={34} fallbackChar={(user?.name || "?")[0]} />
            </Link>
            <button data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden ml-1 p-2 rounded-lg bg-white/5">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 pb-3 hidden lg:flex items-center justify-between">
          {cashOn ? (
            <div data-testid="mode-toggle-switch" className="flex gap-1 p-1 rounded-xl bg-black/40">
              <button data-testid="mode-toggle-coins" onClick={() => navigate("/casino")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${!isCash ? "bg-gradient-to-r from-amber-500 to-purple-600 text-white" : "text-gray-400 hover:text-white"}`}>
                <Coins className="w-4 h-4" /> {t("fun_coins_mode")}
              </button>
              <button data-testid="mode-toggle-cash" onClick={() => navigate("/tournaments")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${isCash ? "bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" : "text-gray-400 hover:text-white"}`}>
                <Trophy className="w-4 h-4" /> {t("cash_skill_mode")}
              </button>
            </div>
          ) : (
            <span data-testid="coins-only-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs font-semibold">
              <Coins className="w-3.5 h-3.5" /> {t("coins_only_no_cash")}
            </span>
          )}
          <nav className="flex items-center gap-1">
            {nav.map((n) => {
              const active = location.pathname === n.to || (n.to === "/casino" && location.pathname.startsWith("/casino"));
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm transition-colors duration-200 ${active ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </Link>
              );
            })}
            <button data-testid="logout-button" onClick={logout} className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200">
              <LogOut className="w-4 h-4" /> {t("logout")}
            </button>
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass border-b border-white/10 overflow-hidden">
            <div className="px-4 py-3 space-y-1">
              {cashOn && (
                <div className="flex gap-2 mb-2">
                  <button onClick={() => { navigate("/casino"); setMenuOpen(false); }} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${!isCash ? "bg-gradient-to-r from-amber-500 to-purple-600" : "bg-white/5"}`}>{t("fun_coins_mode")}</button>
                  <button onClick={() => { navigate("/tournaments"); setMenuOpen(false); }} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${isCash ? "bg-gradient-to-r from-emerald-500 to-cyan-600" : "bg-white/5"}`}>{t("cash_skill_mode")}</button>
                </div>
              )}
              {nav.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-300 hover:bg-white/5">
                  <n.icon className="w-5 h-5" /> {n.label}
                </Link>
              ))}
              <div className="flex items-center justify-between px-3 py-2"><span className="text-sm text-gray-400">Language / Idioma</span><LangBtn /></div>
              <div className="px-3 py-2 pb-20 sm:pb-2"><p className="text-sm text-gray-400 mb-1">Sound &amp; music</p><SoundToggles inline /></div>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-red-400 hover:bg-red-500/10">
                <LogOut className="w-5 h-5" /> {t("logout")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!hideModeBanner && (
        <div className={`text-center py-1.5 text-xs font-medium transition-colors duration-300 ${isCash ? "bg-emerald-500/10 text-emerald-300 border-b border-emerald-500/20" : "bg-amber-500/10 text-amber-300 border-b border-amber-500/20"}`}>
          {isCash ? t("banner_cash") : t("banner_coins")}
        </div>
      )}

      <main className="max-w-[1400px] mx-auto relative z-10">
        <Outlet />
      </main>

      <footer className="max-w-[1400px] mx-auto px-4 pt-6 pb-24 sm:pb-6 mt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
        <BrandLogo size="footer" testid="footer-logo-link" />
        <span data-testid="app-footer-disclaimer">© {new Date().getFullYear()} PhonePlay · Phone Play LLC · A Damani Holdings company · 5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071 · 18+ · Coins have no monetary value · No real-money gambling</span>
        <span className="flex items-center gap-4">
        <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors duration-200 flex items-center gap-1.5">
          <ScrollText className="w-3.5 h-3.5" /> {t("nav_terms")}
        </Link>
        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors duration-200">{t("nav_privacy")}</Link>
        </span>
      </footer>

      <SupportChat />
      <FeedbackButton />
      <ReferralNudge />
      <VoltBubble />
    </div>
  );
}
